/**
 * Browser detection utilities for authentication and device-specific features
 */

/**
 * Detect if the current browser is iOS Safari (including Safari on iPad)
 * Uses both user agent and platform detection for better accuracy
 */
export function isIOSSafari() {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  
  // Check for iOS platforms
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Check for Safari browser (not Chrome or other browsers on iOS)
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(userAgent);
  
  return isIOS && isSafari;
}

/**
 * Detect if the current browser is any WebKit-based browser on iOS
 * This includes Safari, but also embedded WebViews that might have similar limitations
 */
export function isIOSWebKit() {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  
  // Check for iOS platforms (including iPad on iOS 13+)
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Check for WebKit-based browsers (excludes Firefox on iOS)
  const isWebKit = /WebKit/.test(userAgent) && !/FxiOS/.test(userAgent);
  
  return isIOS && isWebKit;
}

/**
 * Check if the browser supports popup authentication
 * Returns false for environments where popups are commonly blocked
 *
 * IMPORTANT: We don't test popup blocking here because:
 * 1. Opening test popups can trigger browser popup blockers
 * 2. Desktop browsers generally support popups fine
 * 3. Mobile browsers are the main concern for popup blocking
 */
export function supportsPopupAuth() {
  // iOS Safari and embedded WebViews have popup restrictions
  if (isIOSWebKit()) {
    return false;
  }

  // For desktop browsers, assume popup support is available
  // Popup authentication will fail gracefully if blocked
  return true;
}

/**
 * Get authentication method recommendation based on browser capabilities
 */
export function getRecommendedAuthMethod() {
  if (isIOSWebKit()) {
    return {
      method: 'redirect',
      reason: 'iOS WebKit browsers work better with redirect authentication'
    };
  }
  
  if (!supportsPopupAuth()) {
    return {
      method: 'redirect',
      reason: 'Popup authentication is blocked or unavailable'
    };
  }
  
  return {
    method: 'popup',
    reason: 'Popup authentication is supported'
  };
}

/**
 * Debug information for troubleshooting authentication issues
 */
export function getBrowserDebugInfo() {
  if (typeof window === 'undefined') {
    return {
      environment: 'server',
      userAgent: 'unknown',
      platform: 'unknown'
    };
  }
  
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    maxTouchPoints: navigator.maxTouchPoints,
    isIOSSafari: isIOSSafari(),
    isIOSWebKit: isIOSWebKit(),
    supportsPopupAuth: supportsPopupAuth(),
    recommendedAuthMethod: getRecommendedAuthMethod(),
    localStorage: (() => {
      try {
        const test = '__test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return 'available';
      } catch (error) {
        return `unavailable: ${error.message}`;
      }
    })(),
    sessionStorage: (() => {
      try {
        const test = '__test__';
        sessionStorage.setItem(test, test);
        sessionStorage.removeItem(test);
        return 'available';
      } catch (error) {
        return `unavailable: ${error.message}`;
      }
    })()
  };
}